// user-journey-tracker.js
import {ref, push, update, get, serverTimestamp, set} from "firebase/database"
import {database, auth} from "../firebase"

// Create a class to handle user journey tracking
class UserJourneyTracker {
    constructor() {
        this._initialized = false
        this._currentSessionId = null
        this._currentPath = []
        this._userId = null
        this._initialClicksTracked = false
        this._initialClicksCount = 0
        this._maxInitialClicks = 5 // Track first 5 clicks
    }

    /**
     * Initialize the tracker with a user ID
     */
    async initialize() {
        // First check if we already have a session ID in localStorage
        const savedSessionId = localStorage.getItem("userJourneySessionId")
        const savedUserId = localStorage.getItem("userJourneyUserId")

        const user = auth.currentUser
        if (!user) {
            console.warn("UserJourneyTracker: No user logged in")
            return
        }

        this._userId = user.uid

        // If we have a saved session ID and it matches the current user, reuse it
        if (savedSessionId && savedUserId === this._userId) {
            this._currentSessionId = savedSessionId
            this._initialized = true

            // Retrieve current path from Firebase to maintain state
            try {
                const pathRef = ref(
                    database,
                    `metrics/userJourneys/${this._userId}/sessions/${this._currentSessionId}/path`
                )
                const snapshot = await get(pathRef)
                if (snapshot.exists()) {
                    this._currentPath = Object.values(snapshot.val())
                }
            } catch (error) {
                console.error("Error retrieving current path:", error)
            }

            await this._checkInitialClicksStatus()
            console.log(
                "UserJourneyTracker reused existing session:",
                this._currentSessionId
            )
            return
        }

        // Otherwise create a new session
        const isNewAccount = this._isNewAccount(user)
        this._currentSessionId = await this._createNewSession(isNewAccount)

        // Save the session ID in localStorage
        localStorage.setItem("userJourneySessionId", this._currentSessionId)
        localStorage.setItem("userJourneyUserId", this._userId)

        this._initialized = true
        await this._checkInitialClicksStatus()
        console.log(
            "UserJourneyTracker initialized with new session:",
            this._currentSessionId
        )
    }

    // Helper to determine if this is a new account
    _isNewAccount(user) {
        // If the user was created less than 5 minutes ago, consider them new
        const fiveMinutesAgo = new Date()
        fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5)

        const creationTime = new Date(user.metadata.creationTime)
        return creationTime > fiveMinutesAgo
    }

    /**
     * Check if initial clicks are already tracked for this user
     */
    async _checkInitialClicksStatus() {
        if (!this._userId) return

        try {
            const initialClicksRef = ref(
                database,
                `metrics/initialClicks/${this._userId}`
            )
            const snapshot = await get(initialClicksRef)

            if (snapshot.exists()) {
                const data = snapshot.val()
                this._initialClicksTracked = data.completed || false
                this._initialClicksCount = data.clicks
                    ? Object.keys(data.clicks).length
                    : 0
            } else {
                // New user - create the initial entry with metadata
                const user = auth.currentUser
                if (user) {
                    await set(initialClicksRef, {
                        email: user.email || null,
                        displayName: user.displayName || null,
                        creationTime: Date.now(), // Current timestamp for today's users filtering
                        completed: false,
                        clicks: {},
                    })
                }
            }
        } catch (error) {
            console.error("Error checking initial clicks status:", error)
        }
    }
    /**
     * Create a new session in Firebase
     */
    async _createNewSession(isNewUser = false) {
        if (!this._userId) return null

        try {
            const sessionsRef = ref(
                database,
                `metrics/userJourneys/${this._userId}/sessions`
            )
            const newSessionRef = push(sessionsRef)

            await update(newSessionRef, {
                startedAt: serverTimestamp(),
                deviceType: this._getDeviceType(),
                userAgent: navigator.userAgent,
                path: [],
                isNewUser: isNewUser, // Use the passed parameter
            })

            return newSessionRef.key
        } catch (error) {
            console.error("Error creating session:", error)
            return null
        }
    }

    /**
     * Track a new step in the user's journey
     * @param {string} screen - The screen name
     * @param {string} action - The action performed
     * @param {Object} additionalData - Any additional data to store
     * @returns {Promise} - A promise that resolves when tracking is complete
     */
    async trackStep(screen, action, additionalData = {}) {
        // Return a promise so callers can await completion
        if (!this._initialized) {
            await this.initialize()
            if (!this._initialized) {
                console.warn("UserJourneyTracker could not initialize")
                return Promise.resolve() // Return resolved promise if initialization failed
            }
        }

        try {
            // Create the step data
            const stepData = {
                screen,
                action,
                timestamp: Date.now(),
                ...additionalData,
            }

            // Add to local path
            this._currentPath.push(stepData)

            // Update in Firebase - store references to all promises
            const promises = []

            // Update session path
            const sessionRef = ref(
                database,
                `metrics/userJourneys/${this._userId}/sessions/${this._currentSessionId}/path`
            )
            promises.push(
                update(sessionRef, {
                    [this._currentPath.length - 1]: stepData,
                })
            )

            // Also add to aggregate paths
            promises.push(this._updateAggregatePatterns(stepData))

            // Track as initial click if needed
            if (
                !this._initialClicksTracked &&
                this._initialClicksCount < this._maxInitialClicks
            ) {
                promises.push(this._trackInitialClick(stepData))
            }

            // Wait for all Firebase operations to complete
            await Promise.all(promises)

            console.log("Tracked step:", stepData)
            return Promise.resolve() // Explicitly return resolved promise
        } catch (error) {
            console.error("Error tracking step:", error)
            return Promise.reject(error) // Allow caller to catch errors
        }
    }

    /**
     * Track an initial click for a new user
     */
    async _trackInitialClick(stepData) {
        try {
            // Don't track more than max clicks
            if (this._initialClicksCount >= this._maxInitialClicks) {
                return
            }

            // Increment the click count
            this._initialClicksCount++

            // Get user info if available
            const user = auth.currentUser
            const userEmail = user ? user.email : null
            const userDisplayName = user ? user.displayName : null

            // Reference to the user's initial clicks data
            const userClicksRef = ref(
                database,
                `metrics/initialClicks/${this._userId}`
            )

            // First check if the user node exists
            const snapshot = await get(userClicksRef)

            if (!snapshot.exists()) {
                // First time seeing this user, create the node with metadata
                await set(userClicksRef, {
                    email: userEmail,
                    displayName: userDisplayName,
                    creationTime: Date.now(),
                    completed: false,
                    clicks: {
                        [this._initialClicksCount]: {
                            ...stepData,
                            ordinal: this._initialClicksCount, // Store which click number this is (1st, 2nd, etc.)
                        },
                    },
                })
            } else {
                // Update just the click data and any needed properties
                await update(userClicksRef, {
                    [`clicks.${this._initialClicksCount}`]: {
                        ...stepData,
                        ordinal: this._initialClicksCount,
                    },
                    // Ensure we have email and creation time
                    ...(userEmail && {email: userEmail}),
                    ...(!snapshot.val().creationTime && {
                        creationTime: Date.now(),
                    }),
                })
            }

            // If we've reached max clicks, mark as completed
            if (this._initialClicksCount >= this._maxInitialClicks) {
                this._initialClicksTracked = true
                await update(userClicksRef, {
                    completed: true,
                    completedAt: Date.now(),
                })
            }
        } catch (error) {
            console.error("Error tracking initial click:", error)
        }
    }

    /**
     * Update the aggregate patterns for analytics
     */
    async _updateAggregatePatterns(stepData) {
        try {
            // Create a simplified path key
            const pathKey = this._currentPath
                .map((step) => `${step.screen}:${step.action}`)
                .join("->")

            // Get the existing patterns
            const patternsRef = ref(database, `metrics/aggregatePatterns`)
            const snapshot = await get(patternsRef)
            const patterns = snapshot.exists() ? snapshot.val() : {}

            // Update the count for this path
            if (patterns[pathKey]) {
                await update(
                    ref(database, `metrics/aggregatePatterns/${pathKey}`),
                    {
                        count: patterns[pathKey].count + 1,
                        lastUpdated: serverTimestamp(),
                    }
                )
            } else {
                // Create new pattern entry
                await update(
                    ref(database, `metrics/aggregatePatterns/${pathKey}`),
                    {
                        path: this._currentPath.map((step) => ({
                            screen: step.screen,
                            action: step.action,
                        })),
                        count: 1,
                        firstSeen: serverTimestamp(),
                        lastUpdated: serverTimestamp(),
                    }
                )
            }

            // Also update initial click patterns separately
            if (!this._initialClicksTracked) {
                await this._updateInitialClickPatterns(stepData)
            }
        } catch (error) {
            console.error("Error updating aggregate patterns:", error)
        }
    }
// Additional method to track a site visit
// Add to UserJourneyTracker class
async trackVisit(source = 'unknown') {
    if (!this._initialized) {
      await this.initialize();
      if (!this._initialized) {
        console.warn("UserJourneyTracker could not initialize for visit tracking");
        return Promise.resolve();
      }
    }
    
    try {
      // Check if this is a new visit or within the same session timeframe
      const lastVisitKey = `lastVisit_${this._userId}`;
      const lastVisit = localStorage.getItem(lastVisitKey);
      const currentTime = Date.now();
      
      // Only count as a new visit if no previous visit or last visit was more than 30 minutes ago
      const isNewVisit = !lastVisit || (currentTime - parseInt(lastVisit)) > 30 * 60 * 1000;
      
      if (isNewVisit) {
        const visitData = {
          timestamp: currentTime,
          source: source,
          userAgent: navigator.userAgent,
          deviceType: this._getDeviceType(),
          sessionId: this._currentSessionId
        };
      
        // Save to the visits collection
        const visitsRef = ref(database, `metrics/visits/${this._userId}`);
        const newVisitRef = push(visitsRef);
        await set(newVisitRef, visitData);
      
        // Update daily visit counter
        const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        const dailyCounterRef = ref(database, `metrics/dailyVisitors/${today}`);
        
        // Get current counter
        const snapshot = await get(dailyCounterRef);
        const currentCount = snapshot.exists() ? snapshot.val().count : 0;
        
        // Update counter
        await set(dailyCounterRef, {
          count: currentCount + 1,
          lastUpdated: serverTimestamp()
        });
      
        // Update last visit timestamp
        localStorage.setItem(lastVisitKey, currentTime.toString());
      
        console.log("Tracked site visit:", source);
      } else {
        console.log("Skipped tracking visit - within session timeout");
      }
      
      return Promise.resolve();
    } catch (error) {
      console.error("Error tracking visit:", error);
      return Promise.reject(error);
    }
  }
  
  // Helper method to detect potential referral source
  _detectSource() {
    // Check referrer
    const referrer = document.referrer;
    if (referrer) {
      if (referrer.includes('tiktok.com')) return 'tiktok';
      if (referrer.includes('instagram.com')) return 'instagram';
      if (referrer.includes('facebook.com')) return 'facebook';
      if (referrer.includes('google.com')) return 'google';
      // Add more referrers as needed
      return referrer;
    }
    
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    if (utmSource) return utmSource;
    
    return 'direct';
  }
    /**
     * Update aggregate patterns specific to initial clicks
     */
    async _updateInitialClickPatterns(stepData) {
        try {
            const clickNumber = this._initialClicksCount
            const actionKey = `${stepData.screen}:${stepData.action}`

            // Update the count for this specific initial click position
            const clickPatternsRef = ref(
                database,
                `metrics/initialClickPatterns/byPosition/${clickNumber}/${actionKey}`
            )
            const snapshot = await get(clickPatternsRef)

            if (snapshot.exists()) {
                await update(clickPatternsRef, {
                    count: snapshot.val().count + 1,
                    lastUpdated: serverTimestamp(),
                })
            } else {
                await set(clickPatternsRef, {
                    screen: stepData.screen,
                    action: stepData.action,
                    count: 1,
                    firstSeen: serverTimestamp(),
                    lastUpdated: serverTimestamp(),
                })
            }
        } catch (error) {
            console.error("Error updating initial click patterns:", error)
        }
    }

    /**
     * Detect device type
     */
    _getDeviceType() {
        const userAgent = navigator.userAgent
        if (
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                userAgent
            )
        ) {
            return "mobile"
        }
        return "desktop"
    }

    /**
     * End the current session
     */
    // Modify the endSession method to clear localStorage
    async endSession() {
        if (!this._initialized || !this._currentSessionId) return

        try {
            const sessionRef = ref(
                database,
                `metrics/userJourneys/${this._userId}/sessions/${this._currentSessionId}`
            )
            await update(sessionRef, {
                endedAt: serverTimestamp(),
                pathLength: this._currentPath.length,
            })

            // Clear session data from localStorage
            localStorage.removeItem("userJourneySessionId")
            localStorage.removeItem("userJourneyUserId")

            this._currentSessionId = null
            this._currentPath = []
            this._initialized = false

            console.log("Session ended and cleared from localStorage")
        } catch (error) {
            console.error("Error ending session:", error)
        }
    }
}

// Create a singleton instance
const userJourneyTracker = new UserJourneyTracker()
export default userJourneyTracker
